import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Table, TableRow, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { EmptyState } from "../shared/EmptyState";
import { ErrorState } from "../shared/ErrorState";
import { StatusBadge } from "../shared/StatusBadge";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { routeStatusMeta } from "../../lib/statusMaps";
import { todayLocal } from "../../lib/utils";
import { useListState } from "../../hooks/useListState";

export function RouteSummaryTab() {
  const [date, setDate] = useListState("reports.routeSummary.date", todayLocal());
  const [routeId, setRouteId] = useListState("reports.routeSummary.routeId", "");
  const [state, setState] = useState({ loading: true, error: null, routes: null });

  const load = () => {
    if (!date && !routeId) {
      setState({ loading: false, error: null, routes: [] });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient.get("/reports/route-summary", { params: { date: date || undefined, route_id: routeId || undefined } })
      .then(({ data }) => setState({ loading: false, error: null, routes: data.routes }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load the route summary."), routes: null }));
  };

  useEffect(load, [date, routeId]);

  return (
    <div className="mt-4 space-y-4" data-testid="route-summary-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="route-summary-date">Date</Label>
          <Input id="route-summary-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="route-summary-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="route-summary-route-id">Route ID (optional)</Label>
          <Input id="route-summary-route-id" type="number" placeholder="e.g. 1829" value={routeId} onChange={(e) => setRouteId(e.target.value)} className="w-40" data-testid="route-summary-route-id-input" />
        </div>
        {(date || routeId) && (
          <Button variant="secondary" size="sm" onClick={() => { setDate(""); setRouteId(""); }} data-testid="route-summary-clear-button">Clear</Button>
        )}
      </div>

      {state.error ? (
        <ErrorState message={state.error} onRetry={load} testId="route-summary-error" />
      ) : state.loading ? (
        <Skeleton className="h-64 w-full" />
      ) : !state.routes || state.routes.length === 0 ? (
        <EmptyState title="No routes found" description="Pick a date and/or route ID to see the delivery breakdown." testId="route-summary-empty" />
      ) : (
        <div className="space-y-3">
          {state.routes.map((r) => {
            const meta = routeStatusMeta(r.status);
            return (
              <Card key={r.route_id} data-testid={`route-summary-card-${r.route_id}`}>
                <CardContent className="p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-heading text-sm font-semibold text-text-main">Route #{r.route_id} — {r.driver_name || "Unassigned"}</p>
                      <p className="text-xs text-text-muted">{r.date} · {r.deliveries_done ?? 0} of {r.deliveries_total ?? 0} delivered
                        {r.admin_delivered > 0 && (
                          <span data-testid={`route-summary-admin-${r.route_id}`}> · {(r.deliveries_done ?? 0) - r.admin_delivered} by driver, {r.admin_delivered} by Admin</span>
                        )}
                      </p>
                    </div>
                    <StatusBadge label={meta.label} variant={meta.variant} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">Plans</p>
                      {r.plan_counts.length === 0 ? <p className="text-xs text-text-muted">—</p> : (
                        <Table>
                          <TableBody>
                            {r.plan_counts.map((p) => (
                              <TableRow key={p.id}><TableCell className="py-1 pl-0">{p.name || `Plan #${p.id}`}</TableCell><TableCell className="py-1 text-right font-semibold">{p.count}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">Addons</p>
                      {r.addon_counts.length === 0 ? <p className="text-xs text-text-muted">—</p> : (
                        <Table>
                          <TableBody>
                            {r.addon_counts.map((a) => (
                              <TableRow key={a.id}><TableCell className="py-1 pl-0">{a.name || `Addon #${a.id}`}</TableCell><TableCell className="py-1 text-right font-semibold">{a.count}</TableCell></TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
